import { BarIndicator } from '@/components/general/activity-indicators';
import { MB_AnimatedChevron, MB_AnimatedChevronRef } from '@/components/general/animated-chevron';
import { MB_Blur, MB_BLUR_TINT } from '@/components/general/blur';
import { MB_Pressable } from '@/components/general/pressable';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import React, { memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { GestureResponderEvent, Platform, ScrollView, StyleProp, StyleSheet, Text, TouchableOpacity, View, ViewStyle, TextStyle } from 'react-native';
import { DataTable } from 'react-native-paper';
import { MB_SVG_EmptyTable } from '@/components/general/svg-components';

const COLORS = {
  lighterGray: '#BAC0CA',
  androidBlurViewOverlayColor: '#ffffff22',
  lightGray: '#E3E3E3',
  darkPurple: '#1D1A31',
  white: '#FFFFFF',
};

interface Cell {
  content: ReactNode;
  conatinerStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  numberOfLines?: number;
}

interface MB_TableProps {
  /**
   * The headers of each column.
   */
  header: Cell[];

  /**
   * The style of the table's header.
   */
  headerStyle?: StyleProp<ViewStyle>;

  hideHeaderWhenEmpty?: boolean;

  /**
   * The data of each row.
   */
  data?: ReactNode[][];

  /**
   * Style of the table.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * Style of the row.
   */
  rowStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the row's text.
   */
  rowTextStyle?: StyleProp<TextStyle>;

  /**
   * Set to true to show a loading indicator.
   */
  loading?: boolean;

  /**
   * Function to get the row's key based on the index.
   */
  keyExtractor?: (index: number) => string;

  /**
   * Callback function to be called when the user right clicks or
   * long presses on a row.
   */
  onRightClickOrTouchHoldForRow?: (index: number, event: React.MouseEvent<HTMLParagraphElement, MouseEvent> | GestureResponderEvent) => void;

  /**
   * Collapsible row component.
   */
  renderCollpsibleArea?: (row: number, isVisble?: boolean) => ReactNode;

  /**
   * Custom component to be rendered when there is no data. If not set,
   * a default one will be displayed.
   */
  EmptyComponent?: React.JSX.Element;

  /**
   * Custom component to be rendered at the end of the table.
   */
  FooterComponent?: React.ComponentType<any> | React.ReactElement | null | undefined;

  /**
   * Set to true to not render the empty data component.
   */
  doNotRenderEmptyData?: boolean;

  loadingScreenProps?: {
    blurTint?: MB_BLUR_TINT;
    androidBlurViewOverlayColor?: string;
    textColor?: string;
  };
}

interface IRowProps {
  /**
   * The index of the row.
   */
  rowIndex: number;

  /**
   * The data of the row.
   */
  row: ReactNode[];

  /**
   * The headers of each column.
   */
  header: Cell[];

  /**
   * Style of the row.
   */
  rowStyle?: StyleProp<ViewStyle>;

  /**
   * Style of the row's text.
   */
  rowTextStyle?: StyleProp<TextStyle>;

  /**
   * The width of the row.
   */
  rowWidth: number;

  /**
   * Collapsible row component.
   */
  renderCollpsibleArea?: (row: number, isVisble?: boolean) => ReactNode;
}

interface RowWrapperProps {
  /**
   * The content of the row.
   */
  children: React.ReactNode;

  /**
   * The index of the row.
   */
  index: number;

  /**
   * Callback function to be called when the user right clicks or
   * long presses on a row.
   */
  onRightClickOrTouchHold?: (index: number, event: React.MouseEvent<HTMLParagraphElement, MouseEvent> | GestureResponderEvent, isLongPress?: boolean) => void;
}

const RowWrapper = memo((props: RowWrapperProps) => {
  if (props.onRightClickOrTouchHold) {
    return (
      <MB_Pressable onLongPress={event => props.onRightClickOrTouchHold?.(props.index, event, true)} onContextMenu={event => props.onRightClickOrTouchHold?.(props.index, event)}>
        {props.children}
      </MB_Pressable>
    );
  }

  return <View>{props.children}</View>;
});

const Row = ({ rowIndex, row, header, rowStyle, rowTextStyle, rowWidth, renderCollpsibleArea }: IRowProps) => {
  const [isCollapsibleAreaVisible, setIsCollapsibleAreaVisible] = useState(false);
  const chevronRef = useRef<MB_AnimatedChevronRef>(null);

  const collapsibleArea = useMemo(() => {
    return renderCollpsibleArea?.(rowIndex, isCollapsibleAreaVisible);
  }, [isCollapsibleAreaVisible, renderCollpsibleArea, rowIndex]);

  const onToggleIsVisible = useCallback(() => {
    setIsCollapsibleAreaVisible(isVisible => {
      if (isVisible) {
        chevronRef.current?.setToInitialRotation();
      } else {
        chevronRef.current?.setToFinalRotation();
      }
      return !isVisible;
    });
  }, []);

  return (
    <View>
      <DataTable.Row style={[styles.row, rowStyle, { minWidth: rowWidth }]}>
        {collapsibleArea && (
          <View style={styles.rowContainer}>
            <TouchableOpacity style={styles.collapsibleButton} onPress={onToggleIsVisible}>
              <MB_AnimatedChevron chevronSize={20} ref={chevronRef} containerStyle={{ paddingHorizontal: 0, paddingVertical: 0 }} />
            </TouchableOpacity>
          </View>
        )}
        {row.map((cellData, cellIndex) =>
          typeof cellData === 'string' || typeof cellData === 'number' ? (
            <View style={[styles.textContainer, header[cellIndex]?.conatinerStyle, rowStyle]} key={cellIndex}>
              <Text numberOfLines={1} selectable={true} style={[styles.textStyle, { textAlign: 'left' }, rowTextStyle]}>
                {cellData}
              </Text>
            </View>
          ) : (
            <View style={[styles.headTitle, styles.rowContainer, header[cellIndex]?.conatinerStyle, rowStyle]} key={cellIndex}>
              {cellData}
            </View>
          )
        )}
      </DataTable.Row>
      {collapsibleArea}
    </View>
  );
};

const EmptyConponentRenderer = ({ EmptyComponent, rowWidth }: { EmptyComponent: React.JSX.Element | undefined; rowWidth: number }) => {
  if (EmptyComponent) {
    return EmptyComponent;
  }

  return (
    <View style={[styles.emptyDataContainer, { width: rowWidth }]}>
      <MB_SVG_EmptyTable />
      <Text style={[{ fontSize: 16, color: COLORS.lighterGray, marginTop: 4 }]}>No data found</Text>
    </View>
  );
};

const MB_Table = ({
  header,
  data,
  headerStyle,
  hideHeaderWhenEmpty,
  style,
  rowStyle,
  rowTextStyle,
  loading,
  keyExtractor,
  onRightClickOrTouchHoldForRow,
  renderCollpsibleArea,
  EmptyComponent,
  FooterComponent,
  doNotRenderEmptyData,
  loadingScreenProps,
}: MB_TableProps) => {
  const handleRightClickOrTouchHold = useCallback(
    (index: number, event: React.MouseEvent<HTMLParagraphElement, MouseEvent> | GestureResponderEvent, isLongPress?: boolean) => {
      if (event.type === 'contextmenu' && (event.nativeEvent as any).pointerType !== 'touch') {
        onRightClickOrTouchHoldForRow?.(index, event);
      } else if (isLongPress) {
        onRightClickOrTouchHoldForRow?.(index, event);
      }

      if (!isLongPress) {
        event.preventDefault();
      }
    },
    [onRightClickOrTouchHoldForRow]
  );

  const [rowWidth, setRowWidth] = React.useState(0);

  const rows = useMemo(() => {
    return data?.map((row, rowIndex) => {
      return (
        <RowWrapper index={rowIndex} key={keyExtractor?.(rowIndex)} onRightClickOrTouchHold={handleRightClickOrTouchHold}>
          <Row rowIndex={rowIndex} row={row} header={header} rowStyle={rowStyle} rowTextStyle={rowTextStyle} rowWidth={rowWidth} renderCollpsibleArea={renderCollpsibleArea} />
        </RowWrapper>
      );
    });
  }, [data, handleRightClickOrTouchHold, header, keyExtractor, renderCollpsibleArea, rowStyle, rowTextStyle, rowWidth]);

  const isEmpty = useMemo(() => {
    return !data || data.length <= 0;
  }, [data]);

  const HeaderComponent = useMemo(() => {
    if (isEmpty && hideHeaderWhenEmpty) {
      return null;
    }
    return (
      <DataTable.Header style={[styles.head, { minWidth: rowWidth, paddingLeft: renderCollpsibleArea ? 50 : undefined }, headerStyle]}>
        {header.map((cellData, cellIndex) => (
          <DataTable.Title textStyle={{ maxHeight: '100%' }} numberOfLines={cellData.numberOfLines} key={cellIndex} style={[styles.headTitle, cellData.conatinerStyle]}>
            {typeof cellData.content === 'string' || typeof cellData.content === 'number' ? (
              <Text numberOfLines={cellData.numberOfLines ?? 1} style={[styles.textStyle, cellData.textStyle]}>
                {cellData.content}
              </Text>
            ) : (
              cellData.content
            )}
          </DataTable.Title>
        ))}
      </DataTable.Header>
    );
  }, [isEmpty, hideHeaderWhenEmpty, header, headerStyle, renderCollpsibleArea, rowWidth]);

  return (
    <DataTable style={[styles.container, style]}>
      <ScrollView onScroll={e => setRowWidth(e.nativeEvent.contentSize.width)} horizontal contentContainerStyle={{ width: rowWidth, flexGrow: 1 }}>
        <View style={{ width: '100%' }}>
          {HeaderComponent}
          {rows}

          {isEmpty && !doNotRenderEmptyData && <EmptyConponentRenderer EmptyComponent={EmptyComponent} rowWidth={rowWidth} />}

          {FooterComponent}
        </View>
      </ScrollView>
      {loading && (
        <MB_Blur
          style={styles.loading}
          tint={loadingScreenProps?.blurTint ?? MB_BLUR_TINT.default}
          androidOverlayColor={loadingScreenProps?.androidBlurViewOverlayColor ?? COLORS.androidBlurViewOverlayColor}
        >
          <BarIndicator color={loadingScreenProps?.textColor ?? COLORS.white} style={{ flex: undefined }} />
          <Text style={[styles.loadingText, { color: loadingScreenProps?.textColor ?? COLORS.white }]}>Loading Data</Text>
        </MB_Blur>
      )}
    </DataTable>
  );
};

export { MB_Table };
export type { MB_TableProps };

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: mbApplyTransparency(COLORS.lightGray, 0.5),
    borderRadius: 10,
    overflow: 'hidden',
  },
  textStyle: {
    fontWeight: '400',
    textAlign: 'left',
    fontSize: 16,
    color: COLORS.white,
  },
  row: {
    borderWidth: 1,
    borderColor: mbApplyTransparency(COLORS.lightGray, 0.5),
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    paddingLeft: 16,
    paddingRight: 16,
    cursor: Platform.OS === 'web' ? 'auto' : undefined,
  },
  head: {
    backgroundColor: COLORS.darkPurple,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    alignItems: 'center',
  },
  headTitle: {
    alignItems: 'center',
    cursor: Platform.OS === 'web' ? 'auto' : undefined,
  },
  emptyDataContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 40,
    paddingBottom: 43,
    paddingHorizontal: 100,
    borderTopWidth: 1,
    borderColor: mbApplyTransparency(COLORS.lightGray, 0.5),
    minWidth: '100%',
  },
  textContainer: {
    alignItems: 'stretch',
    justifyContent: 'center',
    flex: 1,
  },
  rowContainer: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingEnd: 10,
  },
  loading: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  collapsibleButton: {
    backgroundColor: mbApplyTransparency('#B8ABBA', 0.16),
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
  },
  loadingText: {
    fontSize: 44,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 14,
  },
});
